/**
 * Lead schemas define which normalized fields should be displayed per lead type.
 * Schemas are stored in DB, but we keep a fallback map for safe defaults.
 */

export type LeadSchemaField = {
  key: string;
  label: string;
  type?: 'string' | 'number' | 'boolean' | 'date';
};

export type LeadSchema = {
  leadTypeId: string;
  leadTypeName: string;
  fields: LeadSchemaField[];
};

export const DEFAULT_LEAD_SCHEMAS: LeadSchema[] = [
  {
    leadTypeId: 'mortgage_purchase',
    leadTypeName: 'Purchase',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'state', label: 'State' },
      { key: 'goal', label: 'Goal' },
      { key: 'process_stage', label: 'Process Stage' },
      { key: 'employment_status', label: 'Work Status' },
      { key: 'co_borrower', label: 'Co-borrower' },
      { key: 'purchase_price_range', label: 'Price Range' },
      { key: 'credit_quality', label: 'Credit' },
      { key: 'late_payments_12mo', label: 'Late Payments (12mo)' },
      { key: 'collections_or_public_records', label: 'Collections/Public Records' },
      { key: 'new_credit_6mo', label: 'New Credit (6mo)' },
      { key: 'household_income', label: 'Household Income' },
      { key: 'down_payment_savings', label: 'Down Payment Savings' },
      { key: 'utm_source', label: 'UTM Source' },
      { key: 'utm_medium', label: 'UTM Medium' },
      { key: 'utm_campaign', label: 'UTM Campaign' },
      { key: 'utm_content', label: 'UTM Content' },
      { key: 'otp_verified', label: 'OTP Verified', type: 'boolean' },
    ],
  },
  {
    leadTypeId: 'mortgage_refi',
    leadTypeName: 'Refinance',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'state', label: 'State' },
      { key: 'refi_goal', label: 'Refi Goal' },
      { key: 'owns_home', label: 'Owns Home' },
      { key: 'property_type', label: 'Property Type' },
      { key: 'mortgage_balance_est', label: 'Mortgage Balance' },
      { key: 'home_value_est', label: 'Home Value' },
      { key: 'employment_status', label: 'Work Status' },
      { key: 'co_borrower', label: 'Co-borrower' },
      { key: 'credit_quality', label: 'Credit' },
      { key: 'late_payments_12mo', label: 'Late Payments (12mo)' },
      { key: 'collections_or_public_records', label: 'Collections/Public Records' },
      { key: 'new_credit_6mo', label: 'New Credit (6mo)' },
      { key: 'household_income', label: 'Household Income' },
      { key: 'utm_source', label: 'UTM Source' },
      { key: 'utm_medium', label: 'UTM Medium' },
      { key: 'utm_campaign', label: 'UTM Campaign' },
      { key: 'utm_content', label: 'UTM Content' },
      { key: 'otp_verified', label: 'OTP Verified', type: 'boolean' },
    ],
  },
  {
    leadTypeId: 'mortgage_heloc',
    leadTypeName: 'HELOC',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'state', label: 'State' },
      { key: 'equity_goal', label: 'Equity Goal' },
      { key: 'owns_home', label: 'Owns Home' },
      { key: 'timing', label: 'Timing' },
      { key: 'property_state', label: 'Property State' },
      { key: 'property_type', label: 'Property Type' },
      { key: 'home_value_est', label: 'Home Value' },
      { key: 'mortgage_balance_est', label: 'Mortgage Balance' },
      { key: 'age_range', label: 'Age Range' },
      { key: 'credit_quality', label: 'Credit' },
      { key: 'late_payments_12mo', label: 'Late Payments (12mo)' },
      { key: 'collections_or_public_records', label: 'Collections/Public Records' },
      { key: 'household_income', label: 'Household Income' },
      { key: 'utm_source', label: 'UTM Source' },
      { key: 'utm_medium', label: 'UTM Medium' },
      { key: 'utm_campaign', label: 'UTM Campaign' },
      { key: 'utm_content', label: 'UTM Content' },
      { key: 'otp_verified', label: 'OTP Verified', type: 'boolean' },
    ],
  },
  {
    leadTypeId: 'mortgage_reverse',
    leadTypeName: 'Reverse Mortgage',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'state', label: 'State' },
      { key: 'equity_goal', label: 'Equity Goal' },
      { key: 'owns_home', label: 'Owns Home' },
      { key: 'timing', label: 'Timing' },
      { key: 'property_state', label: 'Property State' },
      { key: 'property_type', label: 'Property Type' },
      { key: 'home_value_est', label: 'Home Value' },
      { key: 'mortgage_balance_est', label: 'Mortgage Balance' },
      { key: 'age_range', label: 'Age Range' },
      { key: 'credit_quality', label: 'Credit' },
      { key: 'late_payments_12mo', label: 'Late Payments (12mo)' },
      { key: 'collections_or_public_records', label: 'Collections/Public Records' },
      { key: 'household_income', label: 'Household Income' },
      { key: 'utm_source', label: 'UTM Source' },
      { key: 'utm_medium', label: 'UTM Medium' },
      { key: 'utm_campaign', label: 'UTM Campaign' },
      { key: 'utm_content', label: 'UTM Content' },
      { key: 'otp_verified', label: 'OTP Verified', type: 'boolean' },
    ],
  },
  {
    leadTypeId: 'life_iul',
    leadTypeName: 'IUL',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'state', label: 'State' },
      { key: 'iul_goal', label: 'IUL Goal' },
      { key: 'coverage_amount', label: 'Coverage Amount' },
      { key: 'age', label: 'Age' },
      { key: 'annual_income', label: 'Annual Income' },
      { key: 'monthly_budget', label: 'Monthly Budget' },
      { key: 'health_status', label: 'Health Status' },
      { key: 'tobacco_use', label: 'Tobacco Use' },
      { key: 'utm_source', label: 'UTM Source' },
      { key: 'utm_medium', label: 'UTM Medium' },
      { key: 'utm_campaign', label: 'UTM Campaign' },
      { key: 'utm_content', label: 'UTM Content' },
      { key: 'otp_verified', label: 'OTP Verified', type: 'boolean' },
    ],
  },
  {
    leadTypeId: 'life_mp',
    leadTypeName: 'Mortgage Protection',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'state', label: 'State' },
      { key: 'mp_reason', label: 'Reason' },
      { key: 'has_mortgage_or_buying', label: 'Has Mortgage/Buying' },
      { key: 'mortgage_balance_est', label: 'Mortgage Balance' },
      { key: 'beneficiary', label: 'Beneficiary' },
      { key: 'timeline', label: 'Timeline' },
      { key: 'dob', label: 'Date of Birth' },
      { key: 'health_status', label: 'Health Status' },
      { key: 'utm_source', label: 'UTM Source' },
      { key: 'utm_medium', label: 'UTM Medium' },
      { key: 'utm_campaign', label: 'UTM Campaign' },
      { key: 'utm_content', label: 'UTM Content' },
      { key: 'otp_verified', label: 'OTP Verified', type: 'boolean' },
    ],
  },
  {
    leadTypeId: 'life_fex',
    leadTypeName: 'Final Expense',
    fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'state', label: 'State' },
      { key: 'fex_reason', label: 'Reason' },
      { key: 'applied_life_insurance_before', label: 'Applied for Life Insurance Before' },
      { key: 'age_range', label: 'Age Range' },
      { key: 'coverage_amount', label: 'Coverage Amount' },
      { key: 'monthly_budget', label: 'Monthly Budget' },
      { key: 'timeline', label: 'Timeline' },
      { key: 'health_status', label: 'Health Status' },
      { key: 'utm_source', label: 'UTM Source' },
      { key: 'utm_medium', label: 'UTM Medium' },
      { key: 'utm_campaign', label: 'UTM Campaign' },
      { key: 'utm_content', label: 'UTM Content' },
      { key: 'otp_verified', label: 'OTP Verified', type: 'boolean' },
    ],
  },
];
